<template>
  <div class="page-container vip-approval-page">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <span class="page-title">VIP管理</span>
      </el-breadcrumb-item>
      <el-breadcrumb-item>VIP达标审批</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="page-content" shadow="never">
      <el-form :inline="true" :model="query" size="small" class="query-form">
        <el-form-item label="用户UID">
          <el-input v-model.trim="query.userUid" class="query-input" clearable placeholder="请输入用户UID" />
        </el-form-item>
        <el-form-item label="顶级合伙人UID">
          <el-input v-model.trim="query.topPartnerUid" class="query-input" clearable placeholder="请输入顶级合伙人UID" />
        </el-form-item>
        <el-form-item label="VIP等级">
          <el-select v-model="query.vipLevel" class="query-select" clearable placeholder="请选择VIP等级">
            <el-option
              v-for="item in vipLevels"
              :key="item.code"
              :label="item.label"
              :value="Number(item.code)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="query.approvalStatus" class="query-select" clearable placeholder="请选择状态">
            <el-option
              v-for="item in approvalStatuses"
              :key="item.code"
              :label="item.label"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="达标时间">
          <el-date-picker
            v-model="query.timeRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="timestamp"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">查询</el-button>
          <el-button @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>

      <el-table :data="rows" border size="small" style="width: 100%">
        <el-table-column prop="userUid" label="用户UID" width="120" align="center" />
        <el-table-column prop="userTypeName" label="用户类型" width="120" align="center" />
        <el-table-column prop="topPartnerUid" label="合伙人UID" width="130" align="center">
          <template slot-scope="scope">{{ scope.row.topPartnerUid || '--' }}</template>
        </el-table-column>
        <el-table-column label="VIP等级" width="100" align="center">
          <template slot-scope="scope">VIP{{ scope.row.applyVipLevel }}</template>
        </el-table-column>
        <el-table-column label="用户达标值" min-width="230">
          <template slot-scope="scope">
            <div v-if="matchedThresholds(scope.row).length">
              <div v-for="item in matchedThresholds(scope.row)" :key="item.thresholdType">
                {{ item.thresholdName }}={{ amountText(item.actualValue) }}{{ item.unit ? ' ' + item.unit : '' }}
              </div>
            </div>
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column prop="approvalStatusName" label="状态" width="110" align="center" />
        <el-table-column label="达标时间" width="170">
          <template slot-scope="scope">{{ formatTime(scope.row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.approvalStatus === 'PENDING' && canReview"
              type="text"
              size="small"
              @click="openReview(scope.row)"
            >
              审核
            </el-button>
            <el-button
              v-if="scope.row.approvalStatus !== 'PENDING' && canRecord"
              type="text"
              size="small"
              @click="openRecord(scope.row)"
            >
              审批记录
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next, sizes"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="page"
        :page-sizes="[10, 20, 50]"
        @size-change="onSizeChange"
        @current-change="fetchList"
      />
    </el-card>

    <el-dialog
      title="VIP达标审核"
      :visible.sync="reviewVisible"
      width="760px"
      :close-on-click-modal="false"
      @closed="resetReview"
    >
      <approval-detail :detail="detail" />
      <div class="section-title">审批意见</div>
      <el-form :model="reviewForm" label-width="90px" size="small">
        <el-form-item label="审核结果" required>
          <el-select v-model="reviewForm.approvalStatus" placeholder="请选择审核结果" style="width: 220px">
            <el-option label="审核通过" value="APPROVED" />
            <el-option label="审核拒绝" value="REJECTED" />
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="reviewForm.approvalStatus === 'APPROVED'"
          label="审核备注"
        >
          <el-input
            v-model.trim="reviewForm.approvalOpinion"
            type="textarea"
            :rows="4"
            maxlength="200"
            show-word-limit
            placeholder="请输入审核备注"
          />
        </el-form-item>
        <el-form-item
          v-if="reviewForm.approvalStatus === 'REJECTED'"
          label="拒绝原因"
          required
        >
          <el-input
            v-model.trim="reviewForm.approvalOpinion"
            type="textarea"
            :rows="4"
            maxlength="200"
            show-word-limit
            placeholder="请输入拒绝原因"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="reviewVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitReview">确 定</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="VIP审批记录"
      :visible.sync="recordVisible"
      width="760px"
    >
      <div v-if="detail">
        <div class="section-title">审批记录</div>
        <el-descriptions :column="2" border size="small" class="detail-block">
          <el-descriptions-item label="审核结果">{{ detail.approvalStatusName || '--' }}</el-descriptions-item>
          <el-descriptions-item label="审核人">{{ detail.updateBy || '--' }}</el-descriptions-item>
          <el-descriptions-item label="审核时间">{{ formatTime(detail.updateTime) }}</el-descriptions-item>
          <el-descriptions-item :label="detail.approvalStatus === 'REJECTED' ? '拒绝原因' : '审核备注'">
            {{ detail.approvalOpinion || '--' }}
          </el-descriptions-item>
        </el-descriptions>
        <approval-detail :detail="detail" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Message, MessageBox } from 'element-ui';
import { hasPerm } from 'common/permission';
import api from './api';
import ApprovalDetail from './components/ApprovalDetail.vue';

function emptyQuery() {
  return {
    userUid: '',
    topPartnerUid: '',
    vipLevel: '',
    approvalStatus: '',
    timeRange: [],
  };
}

export default {
  name: 'VipApprovalPage',
  components: { ApprovalDetail },
  data() {
    return {
      query: emptyQuery(),
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      vipLevels: [],
      approvalStatuses: [],
      reviewVisible: false,
      recordVisible: false,
      detail: null,
      reviewForm: {
        approvalStatus: '',
        approvalOpinion: '',
      },
    };
  },
  computed: {
    ...mapState(['userInfo']),
    canReview() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_approval:review');
    },
    canRecord() {
      return hasPerm(this.userInfo || {}, 'tenant:vip_approval:record');
    },
  },
  created() {
    this.loadEnums();
    this.fetchList();
  },
  methods: {
    async loadEnums() {
      const res = await api.enums();
      const data = (res && res.data) || {};
      this.vipLevels = data.vipLevels || [];
      this.approvalStatuses = data.approvalStatuses || [];
    },
    async fetchList() {
      const params = {
        page: this.page,
        size: this.pageSize,
        userUid: this.query.userUid,
        topPartnerUid: this.query.topPartnerUid,
        vipLevel: this.query.vipLevel,
        approvalStatus: this.query.approvalStatus,
      };
      if (this.query.timeRange && this.query.timeRange.length === 2) {
        params.startTime = this.query.timeRange[0];
        params.endTime = this.query.timeRange[1];
      }
      const res = await api.page(params);
      const data = (res && res.data) || {};
      this.rows = data.items || [];
      this.total = data.total || 0;
    },
    search() {
      this.page = 1;
      this.fetchList();
    },
    resetQuery() {
      this.query = emptyQuery();
      this.search();
    },
    onSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.fetchList();
    },
    async openReview(row) {
      await this.loadDetail(row.id);
      this.reviewForm = { approvalStatus: '', approvalOpinion: '' };
      this.reviewVisible = true;
    },
    async openRecord(row) {
      await this.loadDetail(row.id);
      this.recordVisible = true;
    },
    async loadDetail(id) {
      const res = await api.detail(id);
      this.detail = (res && res.data) || null;
    },
    resetReview() {
      this.reviewForm = { approvalStatus: '', approvalOpinion: '' };
      this.detail = null;
    },
    submitReview() {
      if (!this.reviewForm.approvalStatus) {
        Message.warning('请选择审核结果');
        return;
      }
      if (this.reviewForm.approvalStatus === 'REJECTED' && !this.reviewForm.approvalOpinion) {
        Message.warning('请填写拒绝原因');
        return;
      }
      MessageBox.confirm('确认提交审核结果？', '提示', { type: 'warning' })
        .then(() => this.doReview())
        .catch(() => {});
    },
    async doReview() {
      await api.review({
        id: this.detail.id,
        approvalStatus: this.reviewForm.approvalStatus,
        approvalOpinion: this.reviewForm.approvalOpinion || null,
      });
      Message.success('操作成功');
      this.reviewVisible = false;
      this.fetchList();
    },
    matchedThresholds(row) {
      return (row.thresholds || []).filter((item) => item.matched);
    },
    amountText(value) {
      if (value === null || value === undefined || value === '') return '--';
      const text = String(value);
      return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
    },
    formatTime(value) {
      if (!value) return '--';
      let d;
      if (Array.isArray(value)) {
        d = new Date(value[0], value[1] - 1, value[2], value[3] || 0, value[4] || 0, value[5] || 0);
      } else {
        d = new Date(value);
      }
      if (Number.isNaN(d.getTime())) return '--';
      const p = (x) => String(x).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
  },
};
</script>

<style scoped>
.vip-approval-page {
  padding: 16px;
}
.page-title {
  font-weight: 600;
}
.page-content {
  margin-top: 16px;
}
.query-form {
  margin-bottom: 12px;
}
.query-input {
  width: 130px;
}
.query-select {
  width: 120px;
}
/deep/ .query-form .el-date-editor--datetimerange {
  width: 330px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.section-title {
  margin: 14px 0 10px;
  font-weight: 600;
  color: #1f2f5f;
}
.detail-block {
  margin-bottom: 12px;
}
.matched {
  color: #19a35b;
}
.unmatched {
  color: #d93026;
}
</style>
